const SPACING_SCALE = {
    "0": "0px",
    px: "1px",
    "0.5": "0.125rem",
    "1": "0.25rem",
    "1.5": "0.375rem",
    "2": "0.5rem",
    "2.5": "0.625rem",
    "3": "0.75rem",
    "3.5": "0.875rem",
    "4": "1rem",
    "5": "1.25rem",
    "6": "1.5rem",
    "7": "1.75rem",
    "8": "2rem",
    "9": "2.25rem",
    "10": "2.5rem",
    "11": "2.75rem",
    "12": "3rem",
    "14": "3.5rem",
    "16": "4rem",
    "20": "5rem",
    "24": "6rem",
    "28": "7rem",
    "32": "8rem",
    "36": "9rem",
    "40": "10rem",
    "44": "11rem",
    "48": "12rem",
    "52": "13rem",
    "56": "14rem",
    "60": "15rem",
    "64": "16rem",
    "72": "18rem",
    "80": "20rem",
    "96": "24rem",
};

const TEXT_SIZE_SCALE = {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
    "8xl": "6rem",
    "9xl": "8rem",
};

const FONT_WEIGHT_SCALE = {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
};

const RADIUS_SCALE = {
    none: "0px",
    sm: "calc(var(--radius) - 4px)",
    md: "calc(var(--radius) - 2px)",
    lg: "var(--radius)",
    xl: "calc(var(--radius) + 4px)",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
};

const THEME_COLOR_MAP = {
    primary: "var(--primary)",
    "primary-foreground": "var(--primary-foreground)",
    secondary: "var(--secondary)",
    "secondary-foreground": "var(--secondary-foreground)",
    accent: "var(--accent)",
    "accent-foreground": "var(--accent-foreground)",
    muted: "var(--muted)",
    "muted-foreground": "var(--muted-foreground)",
    destructive: "var(--destructive)",
    background: "var(--background)",
    foreground: "var(--foreground)",
    card: "var(--card)",
    "card-foreground": "var(--card-foreground)",
    border: "var(--border)",
    input: "var(--input)",
    ring: "var(--ring)",
    popover: "var(--popover)",
    "popover-foreground": "var(--popover-foreground)",
    sidebar: "var(--sidebar)",
    "sidebar-foreground": "var(--sidebar-foreground)",
    "sidebar-primary": "var(--sidebar-primary)",
    "sidebar-primary-foreground": "var(--sidebar-primary-foreground)",
    "sidebar-accent": "var(--sidebar-accent)",
    "sidebar-accent-foreground": "var(--sidebar-accent-foreground)",
    "sidebar-border": "var(--sidebar-border)",
    "sidebar-ring": "var(--sidebar-ring)",
};

function flattenClassInput(input) {
    if (!input) return [];
    if (Array.isArray(input)) {
        return input.flatMap(flattenClassInput);
    }
    if (typeof input === "string") {
        return input.split(/\s+/).filter(Boolean);
    }
    return [];
}

function stripVariants(token) {
    let bracketDepth = 0;
    let parenDepth = 0;
    let quote = null;
    let lastVariantColon = -1;

    for (let i = 0; i < token.length; i += 1) {
        const char = token[i];

        if (quote) {
            if (char === quote && token[i - 1] !== "\\") {
                quote = null;
            }
            continue;
        }

        if (char === "'" || char === '"') {
            quote = char;
            continue;
        }

        if (char === "[") bracketDepth += 1;
        if (char === "]") bracketDepth = Math.max(0, bracketDepth - 1);
        if (char === "(") parenDepth += 1;
        if (char === ")") parenDepth = Math.max(0, parenDepth - 1);

        if (char === ":" && bracketDepth === 0 && parenDepth === 0) {
            lastVariantColon = i;
        }
    }

    return lastVariantColon >= 0 ? token.slice(lastVariantColon + 1) : token;
}

function getArbitraryValue(token, prefix) {
    const pattern = new RegExp(`^${prefix}-\\[(.+)\\]$`);
    return token.match(pattern)?.[1] ?? null;
}

function resolveThemeColor(value) {
    return THEME_COLOR_MAP[value] ?? value;
}

function normalizeCssValue(value) {
    if (!value) return value;
    return value.replace(/_/g, " ");
}

function resolveSpacing(value) {
    if (!value) return null;
    const arbitraryMatch = value.match(/^\[(.+)\]$/);
    if (arbitraryMatch) return normalizeCssValue(arbitraryMatch[1]);
    if (SPACING_SCALE[value] !== undefined) return SPACING_SCALE[value];
    if (/^-?\d+(\.\d+)?(px|rem|em|%|vh|vw|auto)$/.test(value)) return value;
    return null;
}

function assignSpacing(style, propertyBase, suffix, value) {
    const resolved = resolveSpacing(value);
    if (!resolved) return;

    const map = {
        "": [`${propertyBase}Top`, `${propertyBase}Right`, `${propertyBase}Bottom`, `${propertyBase}Left`],
        x: [`${propertyBase}Left`, `${propertyBase}Right`],
        y: [`${propertyBase}Top`, `${propertyBase}Bottom`],
        t: [`${propertyBase}Top`],
        r: [`${propertyBase}Right`],
        b: [`${propertyBase}Bottom`],
        l: [`${propertyBase}Left`],
    };

    (map[suffix] || []).forEach((prop) => {
        style[prop] = resolved;
    });
}

function assignDimension(style, cssProperty, tokenValue) {
    if (!tokenValue) return;
    const resolvedValue = normalizeCssValue(tokenValue);
    style[cssProperty] = resolvedValue;
}

function parseToken(token, style) {
    if (!token) return;
    const baseToken = stripVariants(token);
    if (baseToken === "flex") style.display = "flex";
    if (baseToken === "grid") style.display = "grid";
    if (baseToken === "hidden") style.display = "none";
    if (baseToken === "flex-row") style.flexDirection = "row";
    if (baseToken === "flex-row-reverse") style.flexDirection = "row-reverse";
    if (baseToken === "flex-col") style.flexDirection = "column";
    if (baseToken === "flex-col-reverse") style.flexDirection = "column-reverse";
    if (baseToken === "flex-wrap") style.flexWrap = "wrap";
    if (baseToken === "flex-nowrap") style.flexWrap = "nowrap";
    if (baseToken === "flex-wrap-reverse") style.flexWrap = "wrap-reverse";
    if (baseToken === "items-start") style.alignItems = "flex-start";
    if (baseToken === "items-center") style.alignItems = "center";
    if (baseToken === "items-end") style.alignItems = "flex-end";
    if (baseToken === "items-stretch") style.alignItems = "stretch";
    if (baseToken === "content-start") style.alignContent = "flex-start";
    if (baseToken === "content-center") style.alignContent = "center";
    if (baseToken === "content-end") style.alignContent = "flex-end";
    if (baseToken === "content-between") style.alignContent = "space-between";
    if (baseToken === "content-around") style.alignContent = "space-around";
    if (baseToken === "content-evenly") style.alignContent = "space-evenly";
    if (baseToken === "content-stretch") style.alignContent = "stretch";
    if (baseToken === "justify-start") style.justifyContent = "flex-start";
    if (baseToken === "justify-center") style.justifyContent = "center";
    if (baseToken === "justify-end") style.justifyContent = "flex-end";
    if (baseToken === "justify-between") style.justifyContent = "space-between";
    if (baseToken === "justify-around") style.justifyContent = "space-around";
    if (baseToken === "justify-evenly") style.justifyContent = "space-evenly";
    if (baseToken === "self-auto") style.alignSelf = "auto";
    if (baseToken === "self-start") style.alignSelf = "flex-start";
    if (baseToken === "self-center") style.alignSelf = "center";
    if (baseToken === "self-end") style.alignSelf = "flex-end";
    if (baseToken === "self-stretch") style.alignSelf = "stretch";
    if (baseToken === "grow") style.flexGrow = 1;
    if (baseToken === "grow-0") style.flexGrow = 0;
    if (baseToken === "shrink") style.flexShrink = 1;
    if (baseToken === "shrink-0") style.flexShrink = 0;
    if (baseToken === "basis-auto") style.flexBasis = "auto";
    if (baseToken === "basis-full") style.flexBasis = "100%";
    if (baseToken === "basis-1/2") style.flexBasis = "50%";
    if (baseToken === "basis-1/3") style.flexBasis = "33.333333%";
    if (baseToken === "basis-1/4") style.flexBasis = "25%";
    if (baseToken === "overflow-hidden") style.overflow = "hidden";
    if (baseToken === "overflow-visible") style.overflow = "visible";
    if (baseToken === "overflow-auto") style.overflow = "auto";
    if (baseToken === "overflow-scroll") style.overflow = "scroll";
    if (baseToken === "object-cover") style.objectFit = "cover";
    if (baseToken === "object-contain") style.objectFit = "contain";
    if (baseToken === "object-fill") style.objectFit = "fill";
    if (baseToken === "whitespace-normal") style.whiteSpace = "normal";
    if (baseToken === "whitespace-nowrap") style.whiteSpace = "nowrap";
    if (baseToken === "whitespace-pre") style.whiteSpace = "pre";
    if (baseToken === "whitespace-pre-wrap") style.whiteSpace = "pre-wrap";
    
    if (baseToken === "break-words") style.overflowWrap = "break-word";
    if (baseToken === "text-start") style.textAlign = "start";
    if (baseToken === "text-center") style.textAlign = "center";
    if (baseToken === "text-end") style.textAlign = "end";
    if (baseToken === "font-thin") style.fontWeight = 100;
    if (baseToken === "font-light") style.fontWeight = 300;
    if (baseToken === "font-normal") style.fontWeight = 400;
    if (baseToken === "font-medium") style.fontWeight = 500;
    if (baseToken === "font-semibold") style.fontWeight = 600;
    if (baseToken === "font-bold") style.fontWeight = 700;

    const widthValue = getArbitraryValue(baseToken, "w");
    if (widthValue) assignDimension(style, "width", widthValue);
    const heightValue = getArbitraryValue(baseToken, "h");
    if (heightValue) assignDimension(style, "height", heightValue);
    const minWidthValue = getArbitraryValue(baseToken, "min-w");
    if (minWidthValue) assignDimension(style, "minWidth", minWidthValue);
    const maxWidthValue = getArbitraryValue(baseToken, "max-w");
    if (maxWidthValue) assignDimension(style, "maxWidth", maxWidthValue);
    const minHeightValue = getArbitraryValue(baseToken, "min-h");
    if (minHeightValue) assignDimension(style, "minHeight", minHeightValue);
    const maxHeightValue = getArbitraryValue(baseToken, "max-h");
    if (maxHeightValue) assignDimension(style, "maxHeight", maxHeightValue);

    if (baseToken === "w-full") style.width = "100%";
    if (baseToken === "w-fit") style.width = "fit-content";
    if (baseToken === "w-5xl") style.width = "1024px";
    if (baseToken === "w-auto") style.width = "auto";
    if (baseToken === "h-auto") style.height = "auto";
    if (baseToken === "h-full") style.height = "100%";
    if (baseToken === "h-screen") style.height = "100vh";
    if (baseToken === "max-w-full") style.maxWidth = "100%";
    if (baseToken === "max-w-none") style.maxWidth = "none";
    if (baseToken === "min-w-0") style.minWidth = "0px";
    if (baseToken === "max-h-full") style.maxHeight = "100%";

    const screenMap = {
        "max-w-screen-sm": "640px",
        "max-w-screen-md": "768px",
        "max-w-screen-lg": "1024px",
        "max-w-screen-xl": "1280px",
    };
    if (screenMap[baseToken]) style.maxWidth = screenMap[baseToken];

    const spacingMatch = baseToken.match(/^([mp])([trblxy]?)-(.+)$/);
    if (spacingMatch) {
        assignSpacing(style, spacingMatch[1] === "p" ? "padding" : "margin", spacingMatch[2], spacingMatch[3]);
    }

    const gapValue = getArbitraryValue(baseToken, "gap");
    if (gapValue) style.gap = normalizeCssValue(gapValue);
    const gapXValue = getArbitraryValue(baseToken, "gap-x");
    if (gapXValue) style.columnGap = normalizeCssValue(gapXValue);
    const gapYValue = getArbitraryValue(baseToken, "gap-y");
    if (gapYValue) style.rowGap = normalizeCssValue(gapYValue);

    const gapMatch = baseToken.match(/^gap(?:-([xy]))?-(.+)$/);
    if (gapMatch && !gapValue && !gapXValue && !gapYValue) {
        const resolved = resolveSpacing(gapMatch[2]);
        if (resolved) {
            if (gapMatch[1] === "x") style.columnGap = resolved;
            else if (gapMatch[1] === "y") style.rowGap = resolved;
            else style.gap = resolved;
        }
    }

    const bgArbitrary = getArbitraryValue(baseToken, "bg");
    if (bgArbitrary) {
        const normalized = normalizeCssValue(bgArbitrary);
        if (/^(url|linear-gradient|radial-gradient|conic-gradient)\(/.test(normalized)) {
            style.backgroundImage = normalized;
        } else {
            style.backgroundColor = normalized;
        }
    }

    if (baseToken.startsWith("bg-") && !bgArbitrary) {
        const value = baseToken.slice(3);

    // 🎨 color
    const resolvedColor = resolveThemeColor(value);
    if (resolvedColor !== value) {
        style.backgroundColor = resolvedColor;
        return;
    }

    // 📏 size
    if (value === "cover") style.backgroundSize = "cover";
    if (value === "contain") style.backgroundSize = "contain";
    if (value === "auto") style.backgroundSize = "auto";

    // 📍 position
    if (value === "center") style.backgroundPosition = "center";
    if (value === "top") style.backgroundPosition = "top";
    if (value === "bottom") style.backgroundPosition = "bottom";
    if (value === "left") style.backgroundPosition = "left";
    if (value === "right") style.backgroundPosition = "right";

    // 🔁 repeat
    if (value === "no-repeat") style.backgroundRepeat = "no-repeat";
    if (value === "repeat") style.backgroundRepeat = "repeat";
    if (value === "repeat-x") style.backgroundRepeat = "repeat-x";
    if (value === "repeat-y") style.backgroundRepeat = "repeat-y";
    }

    const textArbitrary = getArbitraryValue(baseToken, "text");
    if (textArbitrary) {
        const normalized = normalizeCssValue(textArbitrary);
        if (/^-?\d+(\.\d+)?(px|rem|em|%)$/.test(normalized)) {
            style.fontSize = normalized;
        } else {
            style.color = normalized;
        }
    }

    if (baseToken.startsWith("text-") && !textArbitrary) {
        const tokenValue = baseToken.slice(5);
        if (TEXT_SIZE_SCALE[tokenValue]) {
            style.fontSize = TEXT_SIZE_SCALE[tokenValue];
        } else {
            const resolvedColor = resolveThemeColor(tokenValue);
            if (resolvedColor !== tokenValue) {
                style.color = resolvedColor;
            }
        }
    }

    const fontArbitrary = getArbitraryValue(baseToken, "font");
    if (fontArbitrary) {
        style.fontFamily = normalizeCssValue(fontArbitrary);
    }

    if (baseToken.startsWith("font-")) {
        const tokenValue = baseToken.slice(5);
        if (FONT_WEIGHT_SCALE[tokenValue]) {
            style.fontWeight = FONT_WEIGHT_SCALE[tokenValue];
        }
    }

    const roundedArbitrary = getArbitraryValue(baseToken, "rounded");
    if (roundedArbitrary) {
        style.borderRadius = normalizeCssValue(roundedArbitrary);
    }
    if (baseToken.startsWith("rounded-") && !roundedArbitrary) {
        const tokenValue = baseToken.slice(8);
        if (RADIUS_SCALE[tokenValue]) {
            style.borderRadius = RADIUS_SCALE[tokenValue];
        }
    }

    const borderArbitrary = getArbitraryValue(baseToken, "border");
    if (borderArbitrary) {
        const normalized = normalizeCssValue(borderArbitrary);
        if (/^-?\d+(\.\d+)?(px|rem|em)$/.test(normalized)) {
            style.borderWidth = normalized;
        } else {
            style.borderColor = normalized;
        }
    }

    if (baseToken === "border") style.borderWidth = "1px";
    if (baseToken === "border-0") style.borderWidth = "0px";
    if (baseToken === "border-2") style.borderWidth = "2px";
    if (baseToken === "border-4") style.borderWidth = "4px";
    if (baseToken === "border-8") style.borderWidth = "8px";
    if (baseToken === "border-none") style.borderStyle = "none";
    if (baseToken === "border-solid") style.borderStyle = "solid";
    if (baseToken === "border-dashed") style.borderStyle = "dashed";
    if (baseToken === "border-dotted") style.borderStyle = "dotted";
    if (baseToken === "border-double") style.borderStyle = "double";

    if (baseToken.startsWith("border-") && !borderArbitrary) {
        const tokenValue = baseToken.slice(7);
        const resolvedColor = resolveThemeColor(tokenValue);
        if (resolvedColor !== tokenValue) {
            style.borderColor = resolvedColor;
        }
    }

    const shadowArbitrary = getArbitraryValue(baseToken, "shadow");
    if (shadowArbitrary) {
        style.boxShadow = normalizeCssValue(shadowArbitrary);
    }

    if (baseToken === "shadow-none") style.boxShadow = "none";
    if (baseToken === "shadow-sm") style.boxShadow = "0 1px 2px 0 rgb(0 0 0 / 0.05)";
    if (baseToken === "shadow-md") style.boxShadow = "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)";
    if (baseToken === "shadow-lg") style.boxShadow = "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)";

    const zMatch = baseToken.match(/^z-\[?(.+?)\]?$/);
    if (zMatch) {
        const value = zMatch[1];
        if (/^-?\d+$/.test(value)) {
            style.zIndex = Number(value);
        }
    }

    if (baseToken === "order-first") style.order = -9999;
    if (baseToken === "order-last") style.order = 9999;
    if (baseToken === "order-none") style.order = 0;

    const orderMatch = baseToken.match(/^order-\[?(-?\d+)\]?$/);
    if (orderMatch) {
        style.order = Number(orderMatch[1]);
    }
}

export function classToStyle(...classValues) {
    const style = {};
    flattenClassInput(classValues).forEach((token) => parseToken(token, style));
    return style;
}

export default classToStyle;
